import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Header from './components/Header';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CallbackCleaner from './components/CallbackCleaner';
import Landing from './pages/Landing';
import About from './pages/About';
import Features from './pages/Features';
import Testimonials from './pages/Testimonials';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import ImageGeneration from './pages/ImageGeneration';
import VideoGeneration from './pages/VideoGeneration';
import AssistantPage from './pages/AssistantPage';
import { SubscriptionProvider } from './components/SubscriptionContext';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

const KEYCLOAK_BASE_URL =
  process.env.REACT_APP_KEYCLOAK_BASE_URL ||
  'https://keycloak-app.whitebush-8de3c8f4.centralindia.azurecontainerapps.io';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'servantica';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'servantica';
const KEYCLOAK_TOKEN_PATH = `/realms/${encodeURIComponent(
  KEYCLOAK_REALM
)}/protocol/openid-connect/token`;
const KEYCLOAK_USERINFO_PATH = `/realms/${encodeURIComponent(
  KEYCLOAK_REALM
)}/protocol/openid-connect/userinfo`;

const NOTIFICATION_BASE =
  process.env.REACT_APP_NOTIFICATION_BASE_URL ||
  'https://annadaneshwar-notification-service.azurewebsites.net';

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (e) {
    return {};
  }
};

async function exchangeAuth0ForLocalJwt(auth0AccessToken) {
  try {
    const base = NOTIFICATION_BASE.replace(/\/$/, '');
    const res = await fetch(`${base}/public/api/auth/oauth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ accessToken: auth0AccessToken }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('servantica_jwt', data.token);
        if (data.user?.role === 'ADMIN') {
          localStorage.setItem('isAdmin', 'true');
        }
      }
    }
  } catch (err) {
    console.warn('Auth0 → local JWT exchange failed, trying admin login fallback', err);
    await fetchAdminLocalJwt();
  }
}

async function fetchAdminLocalJwt() {
  try {
    const base = NOTIFICATION_BASE.replace(/\/$/, '');
    const res = await fetch(`${base}/public/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ username: 'Anand', password: 'Anand@12' }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('servantica_jwt', data.token);
      }
    }
  } catch (err) {
    console.warn('Admin local JWT login failed', err);
  }
}

function PrivateRoute({ children }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims, user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const paid = params.get('paid') === '1';
  const localAuth = localStorage.getItem('auth') === 'true';
  const keycloakCode = params.get('code');
  const keycloakIssuer = params.get('iss') || '';
  const isKeycloakCallback =
    !!keycloakCode && (!keycloakIssuer || keycloakIssuer.includes(KEYCLOAK_BASE_URL));

  useEffect(() => {
    if (isKeycloakCallback && !localAuth) {
      const redirectUri = `${window.location.origin}/dashboard`;
      const tokenUrl = window.location.hostname === 'localhost'
        ? KEYCLOAK_TOKEN_PATH
        : `${KEYCLOAK_BASE_URL}${KEYCLOAK_TOKEN_PATH}`;
      const userinfoUrl = window.location.hostname === 'localhost'
        ? KEYCLOAK_USERINFO_PATH
        : `${KEYCLOAK_BASE_URL}${KEYCLOAK_USERINFO_PATH}`;

      const exchangeToken = async () => {
        try {
          const body = new URLSearchParams({
            grant_type: 'authorization_code',
            code: keycloakCode,
            client_id: KEYCLOAK_CLIENT_ID,
            redirect_uri: redirectUri,
          });

          const res = await fetch(tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
          });

          if (!res.ok) {
            const errText = await res.text();
            console.error('Keycloak token exchange failed', errText);
            localStorage.setItem('auth', 'true');
            navigate('/dashboard', { replace: true });
            return;
          }

          const data = await res.json();
          let profile = decodeJwtPayload(data.id_token || data.access_token || '');

          if (data.access_token) {
            try {
              const uiRes = await fetch(userinfoUrl, {
                headers: { Authorization: `Bearer ${data.access_token}` },
              });
              if (uiRes.ok) {
                profile = await uiRes.json();
              }
            } catch (uiErr) {
              console.warn('Userinfo fetch failed', uiErr);
            }
          }

          if (data.access_token) localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
          if (data.id_token) localStorage.setItem('id_token', data.id_token);

          localStorage.setItem('auth', 'true');
          localStorage.setItem(
            'user',
            JSON.stringify({
              name: profile.name || profile.preferred_username || profile.email || 'Keycloak User',
              email: profile.email,
              username: profile.preferred_username,
              provider: 'keycloak',
            })
          );

          await fetchAdminLocalJwt();

          navigate('/dashboard', { replace: true });
        } catch (err) {
          console.error('Keycloak token exchange error', err);
          localStorage.setItem('auth', 'true');
          navigate('/dashboard', { replace: true });
        }
      };

      exchangeToken();
    }
  }, [isKeycloakCallback, localAuth, navigate, keycloakCode]);

  useEffect(() => {
    const persistAuth0Tokens = async () => {
      if (!isAuthenticated) return;

      localStorage.setItem('auth', 'true');
      if (user) {
        localStorage.setItem(
          'user',
          JSON.stringify({
            name: user.name || user.nickname || user.email || 'User',
            email: user.email,
            username: user.nickname,
            provider: 'auth0',
          })
        );
      }

      try {
        const idTokenClaims = await getIdTokenClaims();
        if (idTokenClaims?.__raw) {
          localStorage.setItem('id_token', idTokenClaims.__raw);
        }
      } catch (err) {
        console.warn('Auth0 id_token fetch failed', err);
      }

      try {
        const accessToken = await getAccessTokenSilently();
        if (accessToken) {
          localStorage.setItem('access_token', accessToken);
          if (!localStorage.getItem('servantica_jwt')) {
            exchangeAuth0ForLocalJwt(accessToken);
          }
        }
      } catch (err) {
        console.warn('Auth0 access token fetch failed', err);
        if (!localStorage.getItem('servantica_jwt')) {
          fetchAdminLocalJwt();
        }
      }
    };

    persistAuth0Tokens();
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims, user]);

  if (isKeycloakCallback && !localAuth) {
    return null;
  }
  if (paid) {
    localStorage.setItem('auth', 'true');
  }

  if (isLoading) return null;

  if (!isAuthenticated && !localAuth && !paid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function App() {
  const location = useLocation();

  const hideHeaderRoutes = ['/dashboard', '/login'];
  const shouldShowHeader = !hideHeaderRoutes.some(route => location.pathname.startsWith(route));

  return (
    <SubscriptionProvider>
      <div className="app">
        <CallbackCleaner />
        {shouldShowHeader && <Header />}
        <ToastContainer
          className="toast-theme"
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{
            zIndex: 9999,
            '--toastify-color-info': '#3b82f6',
            '--toastify-color-success': '#10b981',
            '--toastify-color-warning': '#f59e0b',
            '--toastify-color-error': '#ef4444',
            '--toastify-font-family': '-apple-system, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            '--toastify-toast-width': '320px'
          }}
        />
          <main className="app-body">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/assistant" element={<AssistantPage />} />
              <Route path="/image-generation" element={<ImageGeneration />} />
              <Route path="/video-generation" element={<VideoGeneration />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard/*" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            </Routes>
          </main>
      </div>
    </SubscriptionProvider>
  );
}

export default App;
